import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, pl as plPL } from 'date-fns/locale';
import { format } from 'date-fns';
import {
  appendBookingSession,
  bookingSessionsOverlap,
  BOOKING_REPEAT_MAX_SESSIONS,
} from '../../utils/bookingRepeatSlots';

interface BookingOccurrenceEditorProps {
  starts: Date[];
  onChange: (starts: Date[]) => void;
  durationMinutes: number;
  disabled?: boolean;
  minutesStep?: number;
  /**
   * When false and there is only one start, render the add button only.
   * The parent keeps its own date and time fields for that first session.
   */
  showRowsWhenSingle?: boolean;
  /** Keep the first session in the parent's date and time fields. */
  omitPrimaryRow?: boolean;
  /** When false, the add control is omitted (e.g. rendered in a dialog footer). */
  showAddButton?: boolean;
  /**
   * `pills` — compact session rows (mobile-style). `cards` — full date/time fields.
   */
  layout?: 'pills' | 'cards';
  /** Optional per-session availability for pill status. */
  isSessionAvailable?: (start: Date, index: number) => boolean;
}

function withDate(start: Date, picked: Date): Date {
  return new Date(
    picked.getFullYear(),
    picked.getMonth(),
    picked.getDate(),
    start.getHours(),
    start.getMinutes(),
    0,
    0,
  );
}

function withTime(start: Date, picked: Date): Date {
  return new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    picked.getHours(),
    picked.getMinutes(),
    0,
    0,
  );
}

export default function BookingOccurrenceEditor({
  starts,
  onChange,
  durationMinutes,
  disabled = false,
  minutesStep = 15,
  showRowsWhenSingle = false,
  omitPrimaryRow = false,
  showAddButton = true,
  layout = 'cards',
  isSessionAvailable,
}: BookingOccurrenceEditorProps) {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const visibleStarts = starts
    .map((start, index) => ({ start, index }))
    .filter(({ index }) => !(omitPrimaryRow && index === 0));
  const showRows = visibleStarts.length > 0 && (starts.length > 1 || showRowsWhenSingle);
  const canAdd =
    showAddButton && !disabled && starts.length > 0 && starts.length < BOOKING_REPEAT_MAX_SESSIONS;
  const overlap = bookingSessionsOverlap(
    starts.map((start) => ({ start, durationMinutes })),
  );

  const localeCode = t('calendar.locale', 'en');
  const locale = localeCode === 'pl' ? plPL : enGB;

  const updateAt = (index: number, next: Date) => {
    onChange(starts.map((start, i) => (i === index ? next : start)));
  };

  const removeAt = (index: number) => {
    if (starts.length < 2) return;
    if (expandedIndex === index) setExpandedIndex(null);
    onChange(starts.filter((_, i) => i !== index));
  };

  const formatSessionRange = (start: Date) => {
    const end = new Date(start.getTime() + Math.max(durationMinutes, 1) * 60_000);
    const day = format(start, 'EEE, d MMM', { locale });
    const from = format(start, 'HH:mm', { locale });
    const to = format(end, 'HH:mm', { locale });
    return `${day} · ${from}–${to}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: layout === 'pills' ? 1 : 1.5, width: '100%' }}>
        {showRows && layout === 'pills' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            {visibleStarts.map(({ start, index }) => {
              const available = isSessionAvailable ? isSessionAvailable(start, index) : !overlap;
              const expanded = expandedIndex === index;
              return (
                <Box key={`session-pill-${index}`}>
                  <Paper
                    elevation={0}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1.5,
                      py: 1.15,
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: expanded ? 'primary.main' : 'divider',
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.05)'
                          : 'background.paper',
                    }}
                  >
                    {available ? (
                      <CheckCircleRoundedIcon color="primary" sx={{ fontSize: 26, flexShrink: 0 }} />
                    ) : (
                      <ErrorOutlineRoundedIcon color="error" sx={{ fontSize: 26, flexShrink: 0 }} />
                    )}
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => setExpandedIndex(expanded ? null : index)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedIndex(expanded ? null : index);
                        }
                      }}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        cursor: disabled ? 'default' : 'pointer',
                        outline: 'none',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {formatSessionRange(start)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: available ? 'primary.main' : 'error.main',
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        {available
                          ? t('booking.sessionAvailable', 'Available')
                          : t('booking.sessionUnavailableShort', 'Unavailable')}
                      </Typography>
                    </Box>
                    <IconButton
                      aria-label={t('booking.editSession', 'Edit session')}
                      onClick={() => setExpandedIndex(expanded ? null : index)}
                      disabled={disabled}
                      size="small"
                    >
                      <ChevronRightIcon
                        sx={{
                          fontSize: 22,
                          transform: expanded ? 'rotate(90deg)' : 'none',
                          transition: 'transform 0.15s ease',
                        }}
                      />
                    </IconButton>
                    <IconButton
                      aria-label={t('booking.removeSession', 'Remove session')}
                      onClick={() => removeAt(index)}
                      disabled={disabled || starts.length < 2}
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                  {expanded ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 1,
                        mt: 1,
                        px: 0.5,
                      }}
                    >
                      <DatePicker
                        label={t('booking.date', 'Date')}
                        value={start}
                        onChange={(picked) => {
                          if (picked) updateAt(index, withDate(start, picked));
                        }}
                        disablePast
                        disabled={disabled}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                      <TimePicker
                        label={t('booking.time', 'Time')}
                        value={start}
                        onChange={(picked) => {
                          if (picked) updateAt(index, withTime(start, picked));
                        }}
                        ampm={false}
                        minutesStep={minutesStep}
                        disabled={disabled}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        ) : null}

        {showRows && layout === 'cards' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              width: '100%',
            }}
          >
            {visibleStarts.map(({ start, index }) => (
              <Paper
                key={`session-${index}`}
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'background.paper',
                  boxShadow: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'none'
                      : '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, flexShrink: 0, minWidth: 64, lineHeight: 1.2 }}
                >
                  {t('booking.sessionNumber', 'Session {{number}}', { number: index + 1 })}
                </Typography>
                <Box sx={{ flex: 1.4, minWidth: 0 }}>
                  <DatePicker
                    label={t('booking.date', 'Date')}
                    value={start}
                    onChange={(picked) => {
                      if (picked) updateAt(index, withDate(start, picked));
                    }}
                    disablePast
                    disabled={disabled}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TimePicker
                    label={t('booking.time', 'Time')}
                    value={start}
                    onChange={(picked) => {
                      if (picked) updateAt(index, withTime(start, picked));
                    }}
                    ampm={false}
                    minutesStep={minutesStep}
                    disabled={disabled}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Box>
                <IconButton
                  aria-label={t('booking.removeSession', 'Remove session')}
                  onClick={() => removeAt(index)}
                  disabled={disabled || starts.length < 2}
                  size="small"
                  sx={{ flexShrink: 0, p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Paper>
            ))}
          </Box>
        ) : null}

        {canAdd ? (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => onChange(appendBookingSession(starts))}
            sx={{
              alignSelf: 'flex-end',
              textTransform: 'uppercase',
              fontWeight: 600,
              borderRadius: 999,
            }}
          >
            {t('booking.addAnotherSession', 'Add session')}
          </Button>
        ) : null}

        {overlap ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {t(
              'booking.sessionsOverlap',
              "These sessions overlap. Choose times that don't run into each other.",
            )}
          </Alert>
        ) : null}
      </Box>
    </LocalizationProvider>
  );
}
